import { PerspectiveCamera } from "@react-three/drei";
import { useMemo } from "react";
import { Vector3 } from "three";
import { cameraOrbitToCartesian } from "../utils";
import type { CartesianPosition, PolarPosition } from "../types";

interface CameraProps {
  initialPosition: PolarPosition;
  center: CartesianPosition;
}

/**
 * Camera component
 *
 * This creates and configures the main 3D camera.
 *
 * Important 3D concepts:
 * - In 3D, a camera must have:
 *   1. A position
 *   2. A direction (what it looks at)
 *
 * - We compute the position using polar coordinates
 *   because it's easier to control orbit-style cameras.
 */
export const Camera = ({ initialPosition, center }: CameraProps) => {
  const position = useMemo(() => {
    const coords = cameraOrbitToCartesian(initialPosition);
    return [coords.x + center.x, coords.y + center.y, coords.z + center.z] as [
      number,
      number,
      number,
    ];
  }, [initialPosition, center]);

  const lookAt = useMemo(() => {
    return new Vector3(center.x, center.y, center.z);
  }, [center]);

  return (
    <PerspectiveCamera
      makeDefault
      /**
       * near and far define the visible depth range.
       * Objects outside this range will not be rendered.
       */
      near={1}
      far={1000}
      position={position}
      /**
       * onUpdate runs after the camera is created.
       * We force it to look at the model center.
       */
      onUpdate={self => self.lookAt(lookAt)}
    />
  );
};
